/**
 * Acquisition source capture — where a new user came from (utm_* / ref on
 * the landing URL), recorded on the Profile at signup.
 *
 * Flow:
 *   1. index.js calls captureAcquisition() at boot, BEFORE MSAL processes
 *      the URL and before any routing can drop the query string. One
 *      localStorage record, first touch wins, 30-day TTL.
 *      Consent-gated (utils/consent.js): the record is written only once
 *      the visitor has accepted — at boot if they accepted earlier, else
 *      held in memory and written when they click "Godta" on this page
 *      view. "Avslå", or withdrawing consent later, drops and deletes it.
 *   2. The record rides as the x-acquisition header (URL-encoded JSON) on the
 *      first authenticated GET /profile (contexts/ProfileContext.js) and on
 *      POST /subscription/start (pages/Subscription.js) — nowhere else.
 *   3. The backend stamps it ONLY when it creates the Profile
 *      (coord-backend lib/tokenIdentity.js) and never on an existing
 *      document, so a stale record is harmless. It is cleared after the
 *      first successful profile response so it stops being resent.
 *
 * Allowlist only: utm_source, utm_medium, utm_campaign, utm_content,
 * utm_term, ref. Click identifiers (gclid, fbclid, …) and every other
 * parameter are ignored on purpose — they are third-party identifiers, not
 * a source.
 *
 * Nothing is ever stored or sent without consent: getAcquisitionHeaders()
 * returns {} unless consent is accepted.
 *
 * localStorage rather than sessionStorage: the record must survive the
 * Vipps in-app-browser return and the hard 402 reload, and MSAL's own
 * account cache already lives there. Every storage access is wrapped
 * (consent.js style) — a storage failure must never affect boot.
 */

import { getConsent, onConsentChange, CONSENT_ACCEPTED } from './consent';

export const ACQUISITION_HEADER = 'x-acquisition';
export const STORAGE_KEY = 'coord-acquisition';
export const ACQUISITION_TTL_MS = 30 * 24 * 60 * 60 * 1000;

// Query parameter → stored field. The field names are the backend's contract
// (Profile.acquisition); landingPath and capturedAt are added at capture.
const PARAM_TO_FIELD = {
  utm_source: 'source',
  utm_medium: 'medium',
  utm_campaign: 'campaign',
  utm_content: 'content',
  utm_term: 'term',
  ref: 'ref',
};

// Mirrors the backend caps (lib/tokenIdentity.js): per value, and for the
// encoded header as a whole. A record past the header cap is dropped.
const VALUE_MAX_LENGTH = 100;
const HEADER_MAX_LENGTH = 2048;

// The landing record while the consent question is open (or was declined
// on this page view). Memory only — it dies with the page.
let pendingRecord = null;
let listeningForConsent = false;

const hasConsent = () => getConsent()?.choice === CONSENT_ACCEPTED;

function removeRecord() {
  try {
    window.localStorage.removeItem(STORAGE_KEY);
  } catch (error) {
    // Storage unavailable — nothing to remove.
  }
}

// First touch wins: never overwrites an unexpired stored record.
function writeRecord(record, now) {
  if (readAcquisition(now)) return null;
  window.localStorage.setItem(STORAGE_KEY, JSON.stringify(record));
  return record;
}

function handleConsentChange(consent) {
  const record = pendingRecord;
  pendingRecord = null;
  if (consent.choice !== CONSENT_ACCEPTED) {
    removeRecord();
    return;
  }
  if (!record) return;
  try {
    writeRecord(record, Date.now());
  } catch (error) {
    // Storage failure — the user simply counts as direct.
  }
}

/**
 * Allowlisted, trimmed, length-capped fields from a query string. Only
 * parameters that are present with a non-empty value appear in the result.
 * @param {string} search - window.location.search (leading '?' optional)
 * @returns {Object} e.g. { source: 'facebook', campaign: 'host2026' }
 */
export function parseAcquisitionParams(search) {
  const fields = {};
  try {
    const params = new URLSearchParams(search || '');
    Object.entries(PARAM_TO_FIELD).forEach(([param, field]) => {
      const value = params.get(param); // first occurrence wins
      if (typeof value !== 'string') return;
      const trimmed = value.trim().slice(0, VALUE_MAX_LENGTH);
      if (trimmed) fields[field] = trimmed;
    });
  } catch (error) {
    // Unparseable query string — treat as no parameters.
  }
  return fields;
}

/**
 * The stored record, or null. An expired or corrupt record is deleted on
 * read so it can never be sent and never blocks a fresh capture.
 * @param {number} [now] - epoch ms (injectable for tests)
 * @returns {Object|null}
 */
export function readAcquisition(now = Date.now()) {
  let raw;
  try {
    raw = window.localStorage.getItem(STORAGE_KEY);
  } catch (error) {
    // Storage unavailable — fail closed: no record.
    return null;
  }
  if (!raw) return null;
  let record = null;
  try {
    record = JSON.parse(raw);
  } catch (error) {
    // Corrupt — treated like an expired record: removed below.
  }
  const capturedAt = record && typeof record === 'object' ? Date.parse(record.capturedAt) : NaN;
  if (!Number.isFinite(capturedAt) || now - capturedAt > ACQUISITION_TTL_MS) {
    removeRecord();
    return null;
  }
  return record;
}

/**
 * Capture at boot. With consent accepted, writes ONE record when the URL
 * carries at least one allowlisted parameter and no unexpired record exists
 * (first touch wins). Without it, writes nothing: the record is held in
 * memory until the consent choice (see handleConsentChange), and a record
 * stored before the current consent version is moved there too, so it
 * still counts as first touch if the visitor accepts. Never throws.
 * @param {{search: string, pathname: string}} [location] - defaults to window.location
 * @param {number} [now] - epoch ms (injectable for tests)
 * @returns {Object|null} the record written, or null when nothing was written
 */
export function captureAcquisition(location = window.location, now = Date.now()) {
  if (!listeningForConsent) {
    listeningForConsent = true;
    onConsentChange(handleConsentChange);
  }
  try {
    const fields = parseAcquisitionParams(location.search);
    const record = Object.keys(fields).length === 0 ? null : {
      ...fields,
      landingPath: location.pathname || '/',
      capturedAt: new Date(now).toISOString(),
    };
    if (!hasConsent()) {
      const earlier = getConsent() ? null : readAcquisition(now);
      removeRecord();
      pendingRecord = earlier || record;
      return null;
    }
    return record ? writeRecord(record, now) : null;
  } catch (error) {
    // Storage failure (private mode, quota, blocked) — the user simply
    // counts as direct. Boot must not care.
    return null;
  }
}

/**
 * Header object for the two call sites: { 'x-acquisition': <value> } when a
 * record exists, otherwise {}. The value is URL-encoded JSON so non-ASCII
 * campaign values are a valid header. capturedAt is not sent — the backend
 * stamps its own server time.
 * @returns {Object}
 */
export function getAcquisitionHeaders() {
  try {
    if (!hasConsent()) return {};
    const record = readAcquisition();
    if (!record) return {};
    const { capturedAt, ...payload } = record;
    const value = encodeURIComponent(JSON.stringify(payload));
    if (value.length > HEADER_MAX_LENGTH) {
      // Cannot have come from captureAcquisition (values are capped) —
      // drop it rather than send something the backend will reject anyway.
      removeRecord();
      return {};
    }
    return { [ACQUISITION_HEADER]: value };
  } catch (error) {
    return {};
  }
}

/** Forget the record — called once a profile response is in. */
export function clearAcquisition() {
  pendingRecord = null;
  removeRecord();
}
